import { SeriesPoint } from './trust-signal-data';

type Props = {
  ci?: number;
  color: string;
  dashed?: boolean;
  data: SeriesPoint[];
  label: string;
  yMax: number;
};

// Value labels hug the ends of the plot so the first can't drift left into the y-axis ticks.
// Axis labels stay centred on their points, which keeps their spacing even.
const valueAnchorFor = (i: number, len: number): 'end' | 'middle' | 'start' =>
  i === 0 ? 'start' : i === len - 1 ? 'end' : 'middle';

export const LineChart = ({ ci, color, dashed, data, label, yMax }: Props) => {
  const W = 360;
  const H = 220;
  const padL = 38;
  // Wide enough for half of the last centred axis label, so it doesn't clip at the viewBox edge
  const padR = 34;
  const padT = 18;
  const padB = 34;
  const iw = W - padL - padR;
  const ih = H - padT - padB;
  const getX = (i: number) => padL + (data.length === 1 ? iw / 2 : (iw * i) / (data.length - 1));
  const getY = (v: number) => padT + ih - (v / yMax) * ih;
  const pts = data.map((d, i) => `${getX(i)},${getY(d.v)}`).join(' ');
  const step = yMax <= 45 ? 15 : 10;
  const ticks = Array.from({ length: Math.floor(yMax / step) + 1 }, (_, i) => i * step);

  return (
    <svg aria-label={label} role="img" viewBox={`0 0 ${W} ${H}`} width="100%">
      {ticks.map((t) => (
        <g key={t}>
          <line
            stroke="var(--xstyled-colors-gray-200)"
            strokeWidth="1"
            x1={padL}
            x2={W - padR}
            y1={getY(t)}
            y2={getY(t)}
          />
          <text fill="var(--xstyled-colors-gray-700)" fontSize="10" textAnchor="end" x={padL - 8} y={getY(t) + 4}>
            {t}%
          </text>
        </g>
      ))}
      {ci !== undefined && (
        <polygon
          fill={color}
          opacity="0.13"
          points={[
            ...data.map((d, i) => `${getX(i)},${getY(Math.min(yMax, d.v + ci))}`),
            ...data
              .slice()
              .reverse()
              .map((d, i) => {
                const idx = data.length - 1 - i;
                return `${getX(idx)},${getY(Math.max(0, d.v - ci))}`;
              }),
          ].join(' ')}
        />
      )}
      <polyline
        fill="none"
        points={pts}
        stroke={color}
        strokeDasharray={dashed ? '6 5' : '0'}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="3"
      />
      {data.map((d, i) => {
        // Push the label further above the point when the next segment rises steeply
        // (label sits above the dot; a steep rise means the line crosses through the label area).
        const nextRise = data[i + 1] ? Math.max(0, getY(d.v) - getY(data[i + 1].v)) : 0;
        const labelOffset = Math.max(6, 6 + 0.25 * nextRise);
        return (
          <g key={d.label}>
            <circle cx={getX(i)} cy={getY(d.v)} fill="#ffffff" r="5" stroke={color} strokeWidth="3" />
            <text
              fill="var(--xstyled-colors-gray-1000)"
              fontSize="10"
              fontWeight="700"
              textAnchor={valueAnchorFor(i, data.length)}
              x={getX(i)}
              y={getY(d.v) - labelOffset - 4}
            >
              {d.v}%
            </text>
            <text fill="var(--xstyled-colors-gray-700)" fontSize="10" textAnchor="middle" x={getX(i)} y={H - 12}>
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
